import { IArticle } from '@/src/interfaces/generated/contentful';
import { Box, Container, Heading } from '@/src/packages/components';
import { JournalEntryProgress } from '@/src/packages/components/pages/JournalEntry/JournalEntryProgress';
import { Breadcrumb } from '@/src/packages/components/ui/Breadcrumb';
import { useInversedNav } from '@/src/packages/components/ui/Navigation';
import { Text } from '@/src/packages/components/ui/Text';
import { paths } from '@/src/packages/routes';
import { cn } from '@/src/packages/utils/cn';
import { getRoute } from '@/src/packages/utils/routes';
import { format, parseISO } from 'date-fns';
import Image from 'next/image';
import { RefObject, useEffect, useState } from 'react';
import { useWindowScroll } from 'react-use';
import { Balancer } from 'react-wrap-balancer';

const StickyHeader = ({
  article,
  contentRef,
}: {
  article: IArticle;
  contentRef: RefObject<HTMLDivElement>;
}) => {
  const [isPinned, setPinned] = useState(false);

  const { y } = useWindowScroll();

  useEffect(() => {
    if (contentRef && contentRef.current) {
      setPinned(y > contentRef.current?.offsetTop);
    }
  }, [y]);

  return (
    <Box
      className={cn(
        'bg-background-primary text-text-primary fixed -top-12 left-0 z-20 flex h-12 w-screen items-center justify-center border-b transition-all',
        {
          'top-0': isPinned,
        },
      )}
    >
      <Heading as="h1" className="-mt-[3px] truncate text-center">
        <Balancer>{article.fields.title}</Balancer>
      </Heading>
      <JournalEntryProgress contentRef={contentRef} />
    </Box>
  );
};

export const JournalEntryHeader = ({
  article,
  contentRef,
}: {
  article: IArticle;
  contentRef: RefObject<HTMLDivElement>;
}) => {
  useInversedNav();

  const backgroundColor = article.fields.colour
    ? article.fields.colour
    : 'forest';

  return (
    <Box
      className="-mt-navigation-height"
      style={{
        background: `linear-gradient(0deg, transparent 25%, var(--colors-${backgroundColor}) 0)`,
      }}
    >
      <StickyHeader article={article} contentRef={contentRef} />

      <Box
        as="header"
        className={cn(
          'flex w-full flex-col items-center justify-center pb-12 pt-[calc(var(--navigation-height)+theme(spacing.12))] text-center text-white',
        )}
      >
        <Container className="space-y-4">
          <Breadcrumb
            className="hidden p-0 lg:inline-flex"
            items={[
              { label: 'Journal', path: getRoute({ route: paths.BLOG_INDEX }) },
              { label: article.fields.title },
            ]}
          />
          <Heading
            as="h1"
            className="font-serif text-3xl leading-tight transition-all duration-500 ease-in-out"
          >
            <Balancer>{article.fields.title}</Balancer>
          </Heading>

          {article.fields.publicationDate && (
            <Box className="opacity-50">
              {format(parseISO(article.fields.publicationDate), 'dd MMM yyyy')}
            </Box>
          )}
        </Container>
      </Box>

      <Container>
        <Box className="space-y-2">
          <Box className="relative aspect-[4/3]">
            <Image
              src={article.fields.cover.fields.file.url}
              alt={
                article.fields.coverCaption
                  ? article.fields.coverCaption
                  : article.fields.title
              }
              fill
              priority
              className="object-cover"
              sizes="100vw"
            />
          </Box>
          {article.fields.coverCaption && (
            <Text className="text-text-tertiary text-center">
              <Balancer>{article.fields.coverCaption}</Balancer>
            </Text>
          )}
        </Box>
      </Container>
    </Box>
  );
};
